import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import './home.scss';
import { getApiToken } from '../../utils/authUtils.js';
import { CardAnalytics } from '../../components/library/card-analytics/CardAnalytics';
import { StatusIndicator, CompetitionStatus } from '../../components';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box';
import { useUser } from '../../contexts/user';

export default function Home() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { selectedTenant } = useUser();
  
  const [athleteStats, setAthleteStats] = useState({});
  const [competitionStats, setCompetitionStats] = useState({});
  const [recentCompetitions, setRecentCompetitions] = useState([]);
  const [upcomingCompetitions, setUpcomingCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      // Token nur einmal abrufen
      console.log('Fetching API token...');
      const token = await getApiToken(instance, accounts);
      console.log('API token fetched:', token);
      // Einziger API-Aufruf für alle Dashboard-Daten
      const response = await fetch(process.env.REACT_APP_serverEndpoint + "/Dashboard/GetDashboard", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const dashboardData = await response.json();

      // Athleten-Statistiken setzen
      setAthleteStats({
        total: dashboardData.athletesTotal,
        active: dashboardData.athletesActive,
        inactive: dashboardData.athletesInactive + dashboardData.athletesPaused
      });

      // Wettkampf-Statistiken setzen
      setCompetitionStats({
        total: dashboardData.competitionsTotal,
        open: dashboardData.competitionsWithEntriesPossible,
        closed: dashboardData.competitionsWithEntriesSent,
        completed: dashboardData.competitionsWithResults
      });

      // Wettkampf-Listen setzen
      setUpcomingCompetitions(dashboardData.upcomingCompetitions || []);
      setRecentCompetitions(dashboardData.lastCompetitions || []);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, selectedTenant]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE');
  };

  // Prüft ob Spalten-Hiding für kleinere Displays aktiviert werden soll
  const isSmallDisplay = () => {
    //return false;
    return window.innerWidth < 700; // Aktiviert für md und xs screens
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Meldung möglich':
        return 'success';
      case 'Meldung abgeschlossen':
        return 'warning';
      case 'Ergebnisse vorhanden':
        return 'normal';
      default:
        return 'normal';
    }
  };

  // Handler für Klick auf eine Wettkampfzeile
  const handleCompetitionRowClick = (e) => {
    const comp = e.data;
    if (!comp) return;
    // Versuche, die ID zu finden (id oder competitionid)
    const compId = comp.competitionId;
    if (compId) {
      navigate(`/competition/edit/${compId}`);
    }
  };

  return (
    <div className="home-scroll-container">
      {loading && (
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          zIndex: 1000 
        }}>
          <LoadIndicator visible={true}  height={60} width={60} />
        </div>
      )}
      {!loading && !error && (
        <ResponsiveBox
         
          screenByWidth={() => {
            if (window.innerWidth < 768) return 'xs';
            if (window.innerWidth < 1200) return 'md';
            return 'lg';
          }}
         
        >
        {/* Für alle Screens exakt vier Rows, gleiche Reihenfolge */}
        <Row ratio={0} baseSize={160} screen="lg md" />
        <Row screen="lg md" />

        <Row baseSize={160} screen="xs" />
        <Row baseSize={120} screen="xs" />
        <Row baseSize={120} screen="xs" />
        <Row baseSize={80} screen="xs" />

        <Col ratio={1} screen="lg md xs" />
        <Col ratio={1} screen="lg md" />

        {/* Statistiken */}
        <Item>
          <Location row={0} col={0} screen="lg" />
          <Location row={0} col={0} screen="md" />
          <Location row={0} col={0} screen="xs" />

          <CardAnalytics title="Wettkämpfe" contentClass="combined-stats-card" menuVisible={false} buttonVisible={true} buttonText='Wettkämpfe anzeigen' buttonLink="/competition/list">
            <div className="stats-container">

              <div className="stat-section">
                <div className="stat-content">
                  <div className="stat-item">
                    <div className="stat-number">{competitionStats.total || 0}</div>
                    <div className="stat-label">Gesamt</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number success">{competitionStats.open || 0}</div>
                    <div className="stat-label">Meldungen möglich</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number warning">{competitionStats.closed || 0}</div>
                    <div className="stat-label">Meldungen versendet</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{competitionStats.completed || 0}</div>
                    <div className="stat-label">Ergebnisse vorhanden</div>
                  </div>

                </div>
              </div>

            </div>
          </CardAnalytics>
        </Item>
        <Item>
          <Location row={0} col={1} screen="lg" />
          <Location row={0} col={1} screen="md" />
         
          <CardAnalytics title="Sportler" contentClass="combined-stats-card" menuVisible={false} buttonVisible={true} buttonText='Sportler anzeigen' buttonLink="/athlete/list" >
            <div className="stats-container">


              <div className="stat-section">

                <div className="stat-content">
                  <div className="stat-item">
                    <div className="stat-number">{athleteStats.total || 0}</div>
                    <div className="stat-label">Gesamt</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number success">{athleteStats.active || 0}</div>
                    <div className="stat-label">Aktiv</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number inactive">{athleteStats.inactive || 0}</div>
                    <div className="stat-label">Inaktiv</div>
                  </div>
                </div>

              </div>
            </div>
          </CardAnalytics>
        </Item>
        {/* Anstehende Wettkämpfe */}
        <Item>
          <Location row={1} col={0} screen="lg"  />
          <Location row={1} col={0} screen="md"  />
          <Location row={2} col={0} screen="xs"  />

          <CardAnalytics title="Anstehende Wettkämpfe" contentClass="competition-card" menuVisible={false} >
            <DataGrid
              dataSource={upcomingCompetitions}
              showBorders={false}
             // columnAutoWidth={true}
              allowColumnResizing={false}
              showColumnHeaders={false}
            
              onRowClick={handleCompetitionRowClick}
            >
              <Column
                dataField="startdate"
                caption="Datum"
                width={70}
                cellRender={(data) => (
                  <div className="competition-date">{formatDate(data.value)}</div>
                )}
                hidingPriority={isSmallDisplay() == true ? 3 : undefined}
              />
              <Column
                dataField="competitionname"
                caption="Name"
           //    width={isSmallDisplay() ? 200 : undefined}
                cellRender={(data) => (
                  <div className="competition-name">{data.value || data.data.title}</div>
                )}
                hidingPriority={isSmallDisplay() == true ? 4 : undefined}
              />
              <Column
                dataField="deadlineIntern"
                caption="MeldeschlussIntern"
                width={70}
                cellRender={(data) => (
                  <div className="competition-date">{formatDate(data.value)}</div>
                )}
                hidingPriority={isSmallDisplay() == true ? 2 : undefined}
              />
              <Column
                dataField="status"
                caption="Status"
                width={150}
                cellRender={CompetitionStatus}
                hidingPriority={isSmallDisplay() == true ? 1 : undefined}
              />
            </DataGrid>
            {upcomingCompetitions.length === 0 && !loading && (
              <div className="no-data">Keine anstehenden Wettkämpfe</div>
            )}
          </CardAnalytics>
        </Item>

        {/* Letzte Wettkämpfe */}
        <Item>
          <Location row={1} col={1} screen="lg"  />
          <Location row={1} col={1} screen="md"  />
          <Location row={3} col={0} screen="xs"  />

          <CardAnalytics title="Letzte Wettkämpfe" contentClass="competition-card" menuVisible={false}>
            <DataGrid
              dataSource={recentCompetitions}
              showBorders={false}
             // columnAutoWidth={true}
              allowColumnResizing={false}
              showColumnHeaders={false}
              columnHidingEnabled={isSmallDisplay()}
              onRowClick={handleCompetitionRowClick}
            >
                <Column
                dataField="startdate"
                caption="Datum"
                width={70}
                cellRender={(data) => (
                  <div className="competition-date">{formatDate(data.value)}</div>
                )}
               hidingPriority={isSmallDisplay() ? 2 : undefined}
              />
              <Column
                dataField="competitionname"
                caption="Name"
        //       width={isSmallDisplay() ? 150 : undefined}
                cellRender={(data) => (
                  <div className="competition-name">{data.value || data.data.title}</div>
                )}
               hidingPriority={isSmallDisplay() ? 3 : undefined}
              />
             
              <Column
                dataField="status"
                caption="Status"
                width={150}
                cellRender={CompetitionStatus}
               hidingPriority={isSmallDisplay() ? 1 : undefined}
              />
            </DataGrid>
            {recentCompetitions.length === 0 && !loading && (
              <div className="no-data">Keine vergangenen Wettkämpfe</div>
            )}
          </CardAnalytics>
        </Item>


      </ResponsiveBox>
      )}
    </div>
  );
}
